// Service Worker registration and management utilities

// Register service worker with error handling
export const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    if (process.env.NODE_ENV === 'development') {
      console.log('Service Worker not supported in this browser');
    }
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/'
    });

    if (process.env.NODE_ENV === 'development') {
      console.log('Service Worker registered successfully:', registration);
    }

    // Handle updates
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      
      if (newWorker) {
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // New content is available
            showUpdateNotification();
          }
        });
      }
    });

    // Handle controller change (new SW activated)
    // DISABLED: This was causing infinite refresh loops
    // navigator.serviceWorker.addEventListener('controllerchange', () => {
    //   window.location.reload();
    // });

    return registration;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Service Worker registration failed:', error);
    }
    return false;
  }
};

// Show update notification to user
const showUpdateNotification = () => {
  const notification = document.createElement('div');
  notification.innerHTML = `
    <div style="
      position: fixed;
      top: 20px;
      right: 20px;
      background: #4CAF50;
      color: white;
      padding: 16px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.1);
      z-index: 10000;
      font-family: Arial, sans-serif;
      max-width: 320px;
    ">
      <div style="font-weight: bold; margin-bottom: 8px;">Update Available!</div>
      <div style="font-size: 14px; margin-bottom: 12px;">A new version is available.</div>
      <button onclick="window.location.reload()" style="
        background: white;
        color: #4CAF50;
        border: none;
        padding: 8px 16px;
        border-radius: 4px;
        cursor: pointer;
        font-weight: bold;
      ">
        Update Now
      </button>
      <button onclick="this.parentElement.parentElement.remove()" style="
        background: transparent;
        color: white;
        border: 1px solid white;
        padding: 8px 16px;
        border-radius: 4px;
        cursor: pointer;
        margin-left: 8px;
      ">
        Later
      </button>
    </div>
  `;
  
  document.body.appendChild(notification);
  
  // Auto-hide after 10 seconds
  setTimeout(() => {
    if (notification.parentElement) {
      notification.remove();
    }
  }, 10000);
};

// Check if app is running offline
export const isOffline = () => {
  return !navigator.onLine;
};

// Listen for online/offline events
export const setupOfflineDetection = (onOnline, onOffline) => {
  window.addEventListener('online', () => {
    if (process.env.NODE_ENV === 'development') {
      console.log('App came online');
    }
    if (onOnline) onOnline();
  });

  window.addEventListener('offline', () => {
    if (process.env.NODE_ENV === 'development') {
      console.log('App went offline');
    }
    if (onOffline) onOffline();
  });
};

// Cache important resources manually
export const precacheResources = async (resources) => {
  if (!('caches' in window)) {
    if (process.env.NODE_ENV === 'development') {
      console.log('Cache API not supported');
    }
    return false;
  }

  try {
    const cache = await caches.open('manual-cache-v1');
    await cache.addAll(resources);
    if (process.env.NODE_ENV === 'development') {
      console.log('Resources precached successfully');
    }
    return true;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Precaching failed:', error);
    }
    return false;
  }
};

// Background sync for form submissions
export const scheduleBackgroundSync = async (tag, data) => {
  if (!('serviceWorker' in navigator) || !('sync' in window.ServiceWorkerRegistration.prototype)) {
    if (process.env.NODE_ENV === 'development') {
      console.log('Background Sync not supported');
    }
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    
    // Store data for sync
    await storeFormData(tag, data);
    
    // Register sync
    await registration.sync.register(tag);
    
    if (process.env.NODE_ENV === 'development') {
      console.log('Background sync scheduled:', tag);
    }
    return true;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Background sync failed:', error);
    }
    return false;
  }
};

// Store form data for background sync
const storeFormData = async (tag, data) => {
  if ('indexedDB' in window) {
    // Use IndexedDB for better offline storage
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('JustOneRepairDB', 1);
      
      request.onerror = () => reject(request.error);
      
      request.onsuccess = (event) => {
        const db = event.target.result;
        const transaction = db.transaction(['formData'], 'readwrite');
        const store = transaction.objectStore('formData');
        
        store.add({
          id: Date.now(),
          tag,
          data,
          timestamp: new Date().toISOString()
        });
        
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
      };
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('formData')) {
          db.createObjectStore('formData', { keyPath: 'id' });
        }
      };
    });
  } else {
    // Fallback to localStorage
    const stored = JSON.parse(localStorage.getItem('pendingForms') || '[]');
    stored.push({
      id: Date.now(),
      tag,
      data,
      timestamp: new Date().toISOString()
    });
    localStorage.setItem('pendingForms', JSON.stringify(stored));
  }
};

// Request notification permission
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    if (process.env.NODE_ENV === 'development') {
      console.log('Notifications not supported');
    }
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  return false;
};

// Show local notification
export const showNotification = (title, options = {}) => {
  if (Notification.permission === 'granted') {
    return new Notification(title, {
      icon: '/icon-192x192.png',
      badge: '/badge-72x72.png',
      ...options
    });
  }
};

// Performance monitoring for service worker
export const monitorServiceWorkerPerformance = () => {
  if ('serviceWorker' in navigator && 'performance' in window) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data && event.data.type === 'CACHE_PERFORMANCE') {
        if (process.env.NODE_ENV === 'development') {
          console.log('Service Worker Cache Performance:', event.data);
        }
      }
    });
  }
};

const serviceWorkerUtils = {
  registerServiceWorker,
  isOffline,
  setupOfflineDetection,
  precacheResources,
  scheduleBackgroundSync,
  requestNotificationPermission,
  showNotification,
  monitorServiceWorkerPerformance
};

export default serviceWorkerUtils;